// Browser and Vite messages for a failed dynamic import or a stylesheet preload.
// Shared by chunk recovery (which still reloads) and the RUM filter (which
// drops the browser's copy of a failure we already handle). Kept free of the
// Datadog SDK so rumUtils tests do not load it.

// HTML served where JS was expected is deploy skew, but the same text is also
// a real parse error. Recovery may reload on it; RUM must not drop it.
const HTML_SERVED_AS_SCRIPT = /unexpected token '<'/i;

const DYNAMIC_IMPORT_FAILURE_PATTERNS = [
  /doesn't provide an export named/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /ChunkLoadError/i,
];

const CSS_PRELOAD_ERROR_PATTERN = /unable to preload css for/i;

/** True when `text` is a browser/Vite dynamic-import fetch or link failure. */
export const isDynamicImportFailureMessage = (text: string): boolean => {
  if (!text) return false;
  return DYNAMIC_IMPORT_FAILURE_PATTERNS.some((pattern) => pattern.test(text));
};

/** True when `text` is HTML parsed as a script (`unexpected token '<'`). */
export const isHtmlServedAsScriptMessage = (text: string): boolean =>
  HTML_SERVED_AS_SCRIPT.test(text);

/**
 * True when `text` is a chunk the app should try to recover by reloading.
 * Includes HTML-as-script, which the RUM filter deliberately does not drop.
 */
export const isChunkLoadErrorMessage = (text: string): boolean =>
  isDynamicImportFailureMessage(text) || isHtmlServedAsScriptMessage(text);

/** True when `text` is Vite's failed-stylesheet-preload error. */
export const isCssPreloadErrorMessage = (text: string): boolean =>
  CSS_PRELOAD_ERROR_PATTERN.test(text);
